import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Job } from '../../../types/job';
import { useAdminStore } from '../../../stores/adminStore';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';

const MIN_REASON_LENGTH = 10;

interface AdminUnpublishJobModalProps {
  job: Job;
  onClose: () => void;
  onSuccess?: () => void;
}

const AdminUnpublishJobModal: React.FC<AdminUnpublishJobModalProps> = ({
  job,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const { adminUnpublishJob } = useAdminStore();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const hasQuotes = (job.quotes_count ?? 0) > 0;

  const reasonError =
    reason.trim().length > 0 && reason.trim().length < MIN_REASON_LENGTH
      ? t('jobModeration.reasonTooShort', {
          ns: 'admin',
          min: MIN_REASON_LENGTH,
        })
      : null;

  const handleConfirm = async () => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setError(
        t('jobModeration.reasonRequired', {
          ns: 'admin',
          min: MIN_REASON_LENGTH,
        })
      );
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const action = await adminUnpublishJob(job.id, reason);
      toast.success(
        action === 'pause'
          ? t('jobModeration.pauseSuccess', { ns: 'admin' })
          : t('jobModeration.unpublishSuccess', { ns: 'admin' })
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError(t('error.generic', { ns: 'common' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[36rem]">
      <h2 className="text-2xl font-bold text-secondary mb-2">
        {hasQuotes
          ? t('jobModeration.pauseTitle', { ns: 'admin' })
          : t('jobModeration.unpublishTitle', { ns: 'admin' })}
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        {hasQuotes
          ? t('jobModeration.pauseExplanation', { ns: 'admin' })
          : t('jobModeration.unpublishExplanation', { ns: 'admin' })}
      </p>

      <div className="mb-4">
        <label
          htmlFor="admin-unpublish-reason"
          className="block text-sm font-semibold text-gray-700 mb-1"
        >
          {t('jobModeration.reasonLabel', { ns: 'admin' })}
        </label>
        <textarea
          id="admin-unpublish-reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={4}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          placeholder={t('jobModeration.reasonPlaceholder', { ns: 'admin' })}
          disabled={saving}
        />
        {reasonError && <p className="mt-1 text-sm text-red-600">{reasonError}</p>}
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>

      <p className="text-xs text-gray-500 mb-6">
        {t('jobModeration.noCreditRefund', { ns: 'admin' })}
      </p>

      <div className="flex flex-row justify-end gap-4">
        <Button variant="simple-text" onClick={onClose} type="button" disabled={saving}>
          {t('form_actions.cancel', { ns: 'common' })}
        </Button>
        <Button variant="primary" onClick={handleConfirm} type="button" disabled={saving}>
          {saving
            ? t('loading', { ns: 'common' })
            : hasQuotes
              ? t('jobModeration.confirmPause', { ns: 'admin' })
              : t('jobModeration.confirmUnpublish', { ns: 'admin' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default AdminUnpublishJobModal;
